import React from 'react'
import { CartesianGrid as RCCartesianGrid, XAxis as RCXAxis, YAxis as RCYAxis, Tooltip } from 'recharts'
import { AxisDomain, AxisInterval, ScaleType } from 'recharts/types/util/types'
import { UnitTypes } from 'src/Libs/types'
import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import moment from '@e3dc-react/shell/Libs/moment'
import { Theme } from '@mui/material'
import { stringWithSIPrefix, STATISTICS_SI_PREFIXES } from 'src/Libs/system-data-handler/utils/formatNumberWithSIPrefix'

/**
 * ##########################################################################################
 * ### Recharts do not support wrapping components                                        ###
 * ### workaround: use Functions instead of functional components!                        ###
 * ### https://github.com/recharts/recharts/issues/412#issuecomment-411077124             ###
 * ##########################################################################################
 */

interface CartesianProps {
  isSmall?: boolean
}
export const CartesianGrid = ({ isSmall = false, ...props }: CartesianProps): React.ReactElement => {
  const dashArray = '3 3'
  return <RCCartesianGrid strokeDasharray={dashArray} stroke="#afafaf" opacity={isSmall ? 0.3 : 1} {...props} syncWithTicks />
}

interface XAxisProps {
  tickFormat: string
  tickFormatter?: (value: unknown) => string
  domain?: AxisDomain
  scale?: ScaleType
  ticks?: number[]
  xAxisInterval?: AxisInterval
  dataKey?: string
  tick: {
    fill: string
  }
  padding?: Record<string, number> // | 'gap' | 'no-gap'
  mirror?: boolean
  isSmall?: boolean
}
export const XAxis = ({ tickFormat, xAxisInterval, mirror, isSmall = false, ticks, ...props }: XAxisProps): React.ReactElement => {
  return (
    <RCXAxis
      allowDuplicatedCategory={true} // has to be off if we use multiple series! otherwise hover is confused wich data object to use
      dataKey="ts"
      tickFormatter={value => moment(value).format(tickFormat)}
      mirror={mirror}
      strokeOpacity={isSmall ? 0.3 : 1}
      opacity={isSmall ? 0.6 : 1}
      interval={xAxisInterval}
      fontSize={11}
      ticks={ticks}
      padding={{ left: 20 }}
      {...props}
    />
  )
}

export const HiddenTooltip = (theme: Theme): React.ReactElement | null => {
  return (
    <Tooltip
      cursor={{
        stroke: theme.palette.grey.grey02,
        strokeWidth: 1,
        strokeDasharray: '5 5',
        fillOpacity: 0.5,
      }}
      content={() => null}
    />
  )
}

interface YAxisProps {
  unit?: string
  minPrefix?: number
  exactPrefix?: number
  tick: {
    fill: string
  }
  domain?: number[]
  mirror?: boolean
  isSmall?: boolean
  isHidden?: boolean
  view?: ChartView
}

export const YAxisLeft = ({ mirror, view, minPrefix = 1, isSmall = false, exactPrefix, ...props }: YAxisProps): React.ReactElement => {
  const unit = view === 'day' ? UnitTypes.watt : UnitTypes.wattHour
  return (
    <RCYAxis
      yAxisId={'left'}
      orientation={'left'}
      tickFormatter={value => stringWithSIPrefix(value, unit, minPrefix, exactPrefix, STATISTICS_SI_PREFIXES)}
      interval={'preserveStartEnd'}
      padding={{ top: 10 }}
      mirror={mirror}
      {...props}
      strokeOpacity={isSmall ? 0.3 : 1}
      opacity={isSmall ? 0.6 : 1}
      fontSize={11}
      strokeWidth={0}
      width={82}
      tickMargin={isSmall ? -10 : -5}
      tick={{ dy: -8, style: { textShadow: '1px 1px 0px white, 1px -1px 0 white,-1px  1px 0 white,-1px -1px 0 white' } }}
    />
  )
}

export const YAxisRight = ({ isHidden = true, mirror, isSmall = false, domain = [0, 100], ...props }: YAxisProps): React.ReactElement => (
  <RCYAxis
    hide={isHidden}
    yAxisId={'right'}
    orientation={'right'}
    unit={' %'}
    domain={domain}
    mirror={mirror}
    width={48}
    strokeOpacity={isSmall ? 0.3 : 1}
    opacity={isSmall ? 0.6 : 1}
    fontSize={11}
    {...props}
  />
)
